import React, { ReactNode } from 'react';
import { StyleSheet } from 'react-native';

import FootballIcon from '@/assets/icons/footballIcon';
import Shield from '@/assets/icons/shield';
import TriangleLeft from '@/assets/icons/triangleLeft';
import { EventTime } from '@/components/EventTime';
import { LiveEventStatus } from '@/components/LiveEventStatus';
import { TeamScore } from '@/components/TeamScore';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { HUD_HEIGHTS } from '@/feature/event-details-sbk/utils/get-hud-height';
import { useEventStatus, useEventWinningSide, useFootballPossession } from '@/hooks/use-sbk-event-details-cache';
import { common } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { isESport, isTennis } from '@/utils/league';
import { renderDate, renderLongDayMonthDate, renderTime } from '@/utils/renderDate';
import { TeamOrder, getSeparatorSymbol, getTeamOrder } from '@/utils/sport-utils';

import { HudTeamSpotlight } from './HudTeamSpotlight';
import { getPrematchHudHeight, getTeamOrderData, hudEventHasPlayerImage } from './utils';

export type HudTeam = {
    shortCode?: string;
    alias?: string;
    record?: string;
    logoUrl?: string;
    logoComponent?: ReactNode;
    primaryColor?: string;
    secondaryColor?: string;
};

export type HudEvent = {
    startTime: number;
    homeTeam: HudTeam;
    awayTeam: HudTeam;
    sport: string;
    league?: League;
};

export type EventHudDetailsProps = {
    pamEventId?: string; // event id from SBK/PAM which is used to subscribe to the event details websocket through Ably to get live updates (scores, match status, etc.)
    event: HudEvent; // Backend agnostic event type. Allows both SBK and Picks to use the same component
};

type EventHudDefaultProps = {
    matchSportSpecificDetails?: ReactNode;
    awayTeamIndicator?: ReactNode;
    homeTeamIndicator?: ReactNode;
    separatorSymbol?: string;
    teamOrder?: TeamOrder;
} & EventHudDetailsProps;

export const LOGO_HEIGHT_WIDTH = 40;

// Top padding for the vs/date column. The taller 75px player image (tennis) pushes it lower than
// the default that centers against a 40px logo.
const SYMBOL_TOP_PADDING = 24;
const PLAYER_IMAGE_SYMBOL_TOP_PADDING = 63;

export const EventHudDetails = ({ event, pamEventId }: EventHudDetailsProps) => {
    // ! add here other "custom" huds for each sport which requires a different one
    if (event.sport === 'American Football') {
        return <EventHudFootball event={event} pamEventId={pamEventId} />;
    }

    return (
        <EventHudDefault
            event={event}
            pamEventId={pamEventId}
            separatorSymbol={getSeparatorSymbol(event.sport)}
            teamOrder={getTeamOrder(event.sport)}
        />
    );
};

const WinTriangle = ({ reversed }: { reversed: boolean }) => (
    <Box style={reversed ? styles.rightTriangle : {}}>
        <TriangleLeft />
    </Box>
);

const EventHudFootball = ({ event, pamEventId = '' }: EventHudDetailsProps) => {
    const { data: pamEvent } = useEvent(pamEventId);
    const eventStatus = useEventStatus(pamEventId) ?? pamEvent?.status;
    const ballPossession = useFootballPossession(pamEvent);
    const isLive = eventStatus === 'LIVE';
    const showHomeTeamPossession = isLive && ballPossession === 'HOME';
    const showAwayTeamPossession = isLive && ballPossession === 'AWAY';

    return (
        <EventHudDefault
            event={event}
            pamEventId={pamEventId}
            awayTeamIndicator={showAwayTeamPossession ? <FootballIcon /> : null}
            homeTeamIndicator={showHomeTeamPossession ? <FootballIcon /> : null}
            matchSportSpecificDetails={
                pamEvent ? <LiveEventStatus event={pamEvent} color="neutral300" variant="bodySmall" /> : null
            }
        />
    );
};

const BetweenScoreSymbols = ({ symbol }: { symbol: string }) => {
    return (
        // minWidth + padding lets "@" render at 24 and "vs" grow to fit two chars.
        <Box
            minWidth={24}
            height={24}
            paddingHorizontal="s6"
            borderRadius="r8"
            alignItems="center"
            justifyContent="center"
            backgroundColor={ds.colorToken.background.neutral.subtler}
        >
            <Text variant="bodySmall" color={ds.colorToken.text.primary}>
                {symbol}
            </Text>
        </Box>
    );
};

type TeamColumnProps = {
    shouldShowFallbackLogo?: boolean;
    primaryColor: string;
    secondaryColor: string;
    team: HudTeam;
    indicator: ReactNode;
    isOpponentWinner: boolean;
    isTennisEvent?: boolean;
    hasPlayerImage?: boolean;
    testID: string;
};

const TENNIS_COLUMN_WIDTH = 100;

const TeamColumn = ({
    shouldShowFallbackLogo,
    primaryColor,
    secondaryColor,
    team,
    indicator,
    isOpponentWinner,
    isTennisEvent,
    hasPlayerImage,
    testID,
}: TeamColumnProps) => {
    const logo = team.logoComponent ? (
        team.logoComponent
    ) : shouldShowFallbackLogo ? (
        <Shield
            primary={primaryColor}
            secondary={secondaryColor}
            width={LOGO_HEIGHT_WIDTH}
            height={LOGO_HEIGHT_WIDTH}
        />
    ) : (
        <Image source={{ uri: team?.logoUrl }} style={styles.logo} resizeMode="contain" />
    );

    return (
        <Box
            paddingTop={hasPlayerImage ? 's20' : 's16'}
            alignItems={'center'}
            flex={isWeb ? 1 : undefined}
            width={!isWeb && isTennisEvent ? TENNIS_COLUMN_WIDTH : undefined}
        >
            {logo}
            <Row justifyContent="center" width={isWeb ? '100%' : 'auto'}>
                <Box
                    position="absolute"
                    top={4}
                    alignItems="center"
                    justifyContent={'center'}
                    style={isWeb ? styles.webTextWrapper : styles.mobileTextWrapper}
                >
                    <Text
                        variant="titleSmall"
                        color={ds.colorToken.text.primary}
                        opacity={isOpponentWinner ? 0.6 : 1}
                        testID={testID}
                        numberOfLines={1}
                        style={isWeb ? common.full : undefined}
                        textAlign={'center'}
                    >
                        {team.shortCode}
                    </Text>
                    <Box position="absolute" right={-20} top={4}>
                        {indicator}
                    </Box>
                </Box>
                <Box position="absolute" top={24} alignItems="center">
                    <Text variant="bodySmall" color="neutral300">
                        {team.record}
                    </Text>
                </Box>
            </Row>
        </Box>
    );
};

const EventHudDefault = ({
    pamEventId = '',
    event,
    awayTeamIndicator,
    homeTeamIndicator,
    matchSportSpecificDetails,
    separatorSymbol = '@',
    teamOrder = ['away', 'home'],
}: EventHudDefaultProps) => {
    const { data: pamEvent } = useEvent(pamEventId);
    const eventStatus = useEventStatus(pamEventId) ?? pamEvent?.status;
    const winningSide = useEventWinningSide(pamEvent);
    const betweenScoreSymbol = isESport(event.league) ? 'vs' : separatorSymbol;
    const isTennisEvent = isTennis(event.league);

    const isPreGame = eventStatus !== 'LIVE' && eventStatus !== 'FINISHED';
    const isFinished = eventStatus === 'FINISHED';
    const isLive = eventStatus === 'LIVE';
    const startDate = new Date(event.startTime);
    // A player image (75px) is taller than a team logo (40px), so it drives the column vertical rhythm.
    const hasPlayerImage = hudEventHasPlayerImage(event);
    const height = isPreGame ? getPrematchHudHeight(event) : HUD_HEIGHTS.liveDetailsSlide;
    const betweenScoreTopPadding = hasPlayerImage ? PLAYER_IMAGE_SYMBOL_TOP_PADDING : SYMBOL_TOP_PADDING;
    const { left, right, draw } = getTeamOrderData(event, teamOrder, winningSide, {
        home: homeTeamIndicator,
        away: awayTeamIndicator,
    });

    return (
        <Box
            position="relative"
            height={height}
            alignItems={isWeb ? undefined : 'center'}
            paddingHorizontal={!isWeb && isTennisEvent ? 's16' : undefined}
        >
            {isWeb ? (
                <>
                    <HudTeamSpotlight
                        type="away"
                        primaryColor={left.colors.primary}
                        secondaryColor={left.colors.secondary}
                    />
                    <HudTeamSpotlight
                        type="home"
                        primaryColor={right.colors.primary}
                        secondaryColor={right.colors.secondary}
                    />
                </>
            ) : null}
            <Row maxWidth={isWeb || isTennisEvent ? undefined : 304}>
                <TeamColumn
                    shouldShowFallbackLogo={left.colors.shouldShowFallbackLogo}
                    primaryColor={left.colors.primary}
                    secondaryColor={left.colors.secondary}
                    team={left.team}
                    indicator={left.indicator}
                    isOpponentWinner={right.isWinner}
                    isTennisEvent={isTennisEvent}
                    hasPlayerImage={hasPlayerImage}
                    testID="leftTeam"
                />
                <Box flex={1}>
                    {isPreGame ? (
                        <Box flex={1} alignItems={'center'} style={{ paddingTop: betweenScoreTopPadding }}>
                            <BetweenScoreSymbols symbol={betweenScoreSymbol} />
                            {isTennisEvent ? (
                                <Text variant="bodySmall" marginTop="s12" color="neutral300">
                                    {renderDate(startDate)}
                                </Text>
                            ) : (
                                <>
                                    <Text variant="bodySmall" marginTop="s12" color="neutral300">
                                        {renderLongDayMonthDate(startDate)}
                                    </Text>
                                    <Text variant="bodySmall" color="neutral300">
                                        {renderTime(startDate)}
                                    </Text>
                                </>
                            )}
                        </Box>
                    ) : (
                        <Box paddingTop="s16">
                            <Row alignItems={'center'} alignSelf={'center'} height={LOGO_HEIGHT_WIDTH}>
                                <Box
                                    alignItems={'center'}
                                    justifyContent={'flex-start'}
                                    width={84.5}
                                    height={34}
                                    pb="s2"
                                    mx="s12"
                                >
                                    {pamEvent ? (
                                        <TeamScore
                                            event={pamEvent}
                                            side={left.scoreSide}
                                            fontSize={32}
                                            lineHeight={35}
                                            fontFamily="Gamay-CondSemiBold"
                                        />
                                    ) : null}
                                </Box>
                                {isFinished && !draw ? (
                                    <WinTriangle reversed={right.isWinner} />
                                ) : (
                                    <Box style={styles.scoreDash} />
                                )}
                                <Box
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    height={34}
                                    width={84.5}
                                    pb="s2"
                                    mx="s12"
                                >
                                    {pamEvent ? (
                                        <TeamScore
                                            event={pamEvent}
                                            side={right.scoreSide}
                                            fontSize={32}
                                            lineHeight={35}
                                            fontFamily="Gamay-CondSemiBold"
                                        />
                                    ) : null}
                                </Box>
                            </Row>
                            <Box alignItems={'center'} justifyContent={'center'} height={40} mt="s4">
                                {pamEvent ? (
                                    <EventTime
                                        event={pamEvent}
                                        variant="labelLarge"
                                        color={
                                            isLive
                                                ? ds.colorToken.text.danger
                                                : isFinished
                                                ? 'neutral300'
                                                : ds.colorToken.text.primary
                                        }
                                    />
                                ) : null}
                                {matchSportSpecificDetails}
                            </Box>
                        </Box>
                    )}
                </Box>
                <TeamColumn
                    shouldShowFallbackLogo={right.colors.shouldShowFallbackLogo}
                    primaryColor={right.colors.primary}
                    secondaryColor={right.colors.secondary}
                    team={right.team}
                    indicator={right.indicator}
                    isOpponentWinner={left.isWinner}
                    isTennisEvent={isTennisEvent}
                    hasPlayerImage={hasPlayerImage}
                    testID="rightTeam"
                />
            </Row>
        </Box>
    );
};

const styles = StyleSheet.create({
    logo: {
        width: LOGO_HEIGHT_WIDTH,
        height: LOGO_HEIGHT_WIDTH,
    },
    scoreDash: {
        width: 8,
        height: 2,
        backgroundColor: ds.colorValue.background.neutral.inverse,
    },
    rightTriangle: {
        transform: [
            {
                rotate: '180deg',
            },
        ],
    },
    webTextWrapper: {
        width: '100%',
        paddingHorizontal: 16,
    },
    mobileTextWrapper: {
        maxWidth: 100,
    },
});
